import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, Search, Calendar, Eye, Filter,
  ChevronLeft, ChevronRight, Loader2, Inbox, Download, FileSpreadsheet
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format field values nicely
const formatFieldValue = (value, key) => {
  if (value === null || value === undefined) return 'N/A';
  
  // Handle name objects (first/last)
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value.first !== undefined || value.last !== undefined) {
      return `${value.first || ''} ${value.last || ''}`.trim() || 'N/A';
    }
    // Handle address objects
    if (value.street !== undefined || value.city !== undefined) {
      const parts = [value.street, value.city, value.state, value.zip, value.country].filter(Boolean);
      return parts.join(', ') || 'N/A';
    }
    // Handle other objects - display as key-value pairs
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  return String(value);
};

export const SubmissionsPage = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const limit = 20;

  const fetchSubmissions = useCallback(async () => {
    const authToken = token || localStorage.getItem('token');
    if (!authToken) return;

    try {
      let url = `${API_URL}/api/submissions?skip=${page * limit}&limit=${limit}`;
      if (selectedFormId && selectedFormId !== 'all') {
        url += `&form_id=${selectedFormId}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
        setTotal(data.total || 0);
        setUserRole(data.user_role || '');
        
        // Use forms list from API
        if (data.forms && data.forms.length > 0) {
          setForms(data.forms);
        }
      }
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [token, page, selectedFormId]);

  useEffect(() => {
    if (token) {
      fetchSubmissions();
    }
  }, [token, fetchSubmissions]);

  const filteredSubmissions = submissions.filter(sub => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      sub.form_title?.toLowerCase().includes(searchLower) ||
      JSON.stringify(sub.data).toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getSubmissionPreview = (data) => {
    if (!data) return '';
    const entries = Object.entries(data).slice(0, 2);
    return entries.map(([key, value]) => {
      // Skip displaying base64 image data in preview
      if (typeof value === 'string' && value.startsWith('data:image')) {
        return `${key}: [Signature]`;
      }
      const formatted = formatFieldValue(value, key);
      return `${key}: ${formatted.substring(0, 25)}${formatted.length > 25 ? '...' : ''}`;
    }).join(' | ');
  };

  const handleFormFilterChange = (value) => {
    setSelectedFormId(value);
    setPage(0); // Reset to first page when filter changes
  };

  // Download submission as branded PDF — uses the backend signed-document endpoint
  // (same as the post-submission "Download" button). Single source of truth.
  const handleDownloadPDF = async (submission) => {
    if (!submission?.document_number) {
      toast.error('PDF not available for this submission');
      return;
    }
    try {
      toast.info('Preparing PDF...');
      const resp = await fetch(`${API_URL}/api/signed-documents/${submission.document_number}/pdf`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${submission.form_title || 'submission'}_${submission.document_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  // Download submission as CSV
  const handleDownloadCSV = (submission) => {
    const data = submission.data || {};
    
    // Build CSV content
    let csvContent = 'Field,Value\n';
    
    Object.entries(data).forEach(([key, value]) => {
      let displayValue = '';
      
      // Handle signature (base64 image)
      if (typeof value === 'string' && value.startsWith('data:image')) {
        displayValue = '[Signature Image]';
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        if (value.first !== undefined || value.last !== undefined) {
          displayValue = `${value.first || ''} ${value.last || ''}`.trim();
        } else if (value.street !== undefined) {
          displayValue = [value.street, value.city, value.state, value.zip, value.country].filter(Boolean).join(', ');
        } else {
          displayValue = JSON.stringify(value);
        }
      } else if (Array.isArray(value)) {
        displayValue = value.join('; ');
      } else {
        displayValue = String(value || '');
      }
      
      // Escape quotes and wrap in quotes
      displayValue = `"${displayValue.replace(/"/g, '""')}"`;
      csvContent += `"${key.replace(/"/g, '""')}",${displayValue}\n`;
    });
    
    // Add metadata
    csvContent += `\n"Form Title","${(submission.form_title || 'Unknown').replace(/"/g, '""')}"\n`;
    csvContent += `"Submitted At","${formatDate(submission.submitted_at)}"\n`;
    csvContent += `"Submission ID","${submission.id || ''}"\n`;
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${submission.form_title || 'submission'}_${submission.id?.slice(0, 8) || 'export'}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    toast.success('CSV downloaded successfully!');
  };

  // Export ALL submissions as CSV (for selected form or all forms)
  const handleExportAllCSV = async (formIdToExport) => {
    try {
      toast.info('Fetching submissions...');
      
      // Fetch all submissions for the selected form (or all forms)
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '10000'); // Get all submissions
      if (formIdToExport && formIdToExport !== 'all') {
        params.append('form_id', formIdToExport);
      }
      
      const response = await fetch(`${API_URL}/api/submissions?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }
      
      const data = await response.json();
      const exportSubmissions = data.submissions || [];
      
      if (exportSubmissions.length === 0) {
        toast.error('No submissions to export');
        return;
      }
      
      // Get all unique field keys from all submissions
      const allKeys = new Set();
      exportSubmissions.forEach(sub => {
        Object.keys(sub.data || {}).forEach(key => allKeys.add(key));
      });
      const fieldKeys = Array.from(allKeys);
      
      // Build CSV header
      let csvContent = ['Form', 'Submitted At', ...fieldKeys].map(h => `"${h}"`).join(',') + '\n';
      
      // Build CSV rows
      exportSubmissions.forEach(submission => {
        const rowData = submission.data || {};
        const row = [
          submission.form_title || 'Unknown',
          formatDate(submission.submitted_at)
        ];
        
        // Add each field value
        fieldKeys.forEach(key => {
          let value = rowData[key];
          
          // Handle special types
          if (typeof value === 'string' && value.startsWith('data:image')) {
            value = '[Signature Image]';
          } else if (typeof value === 'object' && !Array.isArray(value)) {
            if (value?.first !== undefined || value?.last !== undefined) {
              value = `${value.first || ''} ${value.last || ''}`.trim();
            } else if (value?.street !== undefined) {
              value = [value.street, value.city, value.state, value.zip, value.country].filter(Boolean).join(', ');
            } else {
              value = JSON.stringify(value);
            }
          } else if (Array.isArray(value)) {
            value = value.join('; ');
          }
          
          row.push(value || '');
        });
        
        // Escape and add row
        csvContent += row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',') + '\n';
      });
      
      // Get form name for filename
      let fileName = 'all_submissions';
      if (formIdToExport && formIdToExport !== 'all') {
        const selectedForm = forms.find(f => f.id === formIdToExport);
        if (selectedForm) {
          fileName = selectedForm.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        }
      }
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `${fileName}_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success(`Exported ${exportSubmissions.length} submissions to CSV`);
    } catch (error) {
      toast.error('Failed to export submissions');
      console.error(error);
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Submissions</h1>
            <p className="text-slate-600">
              View all form submissions
              {userRole && <Badge variant="outline" className="ml-2 capitalize">{userRole.replace('_', ' ')}</Badge>}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Export All Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-all-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export All
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => handleExportAllCSV('all')}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  All Forms ({total} submissions)
                </DropdownMenuItem>
                {forms.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 border-t mt-1 pt-2">
                      Export by Form
                    </div>
                    {forms.map(form => (
                      <DropdownMenuItem key={form.id} onClick={() => handleExportAllCSV(form.id)}>
                        <FileText className="w-4 h-4 mr-2" />
                        {form.title}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Form Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={selectedFormId} onValueChange={handleFormFilterChange}>
                <SelectTrigger className="w-[200px]" data-testid="form-filter">
                  <SelectValue placeholder="All Forms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Forms</SelectItem>
                  {forms.map(form => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-submissions"
              />
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {filteredSubmissions.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No submissions yet</h3>
              <p className="text-slate-500">
                {selectedFormId !== 'all' 
                  ? 'No submissions for the selected form' 
                  : 'Form submissions will appear here'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Form</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Submitted At</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Preview</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{submission.form_title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(submission.submitted_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-500 truncate max-w-xs">
                          {getSubmissionPreview(submission.data)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`download-${submission.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Export
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDownloadPDF(submission)}>
                                <FileText className="w-4 h-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadCSV(submission)}>
                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                                Download CSV
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSubmission(submission)}
                            data-testid={`view-submission-${submission.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-slate-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Submission Detail Modal */}
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Submission Details</span>
                {selectedSubmission && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="ml-4">
                        <Download className="w-4 h-4 mr-1" />
                        Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleDownloadPDF(selectedSubmission)}>
                        <FileText className="w-4 h-4 mr-2" />
                        Download PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadCSV(selectedSubmission)}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Download CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedSubmission && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Form</p>
                  <p className="font-medium">{selectedSubmission.form_title}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Submitted At</p>
                  <p className="font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                </div>
                <div className="border rounded-lg divide-y">
                  {Object.entries(selectedSubmission.data || {}).map(([key, value]) => (
                    <div key={key} className="p-4">
                      <p className="text-sm text-slate-500 mb-1">{key}</p>
                      {typeof value === 'string' && value.startsWith('data:image') ? (
                        <img src={value} alt="Signature" className="max-w-[250px] max-h-[120px] border rounded" />
                      ) : (
                        <p className="font-medium text-slate-900">
                          {formatFieldValue(value, key)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SubmissionsPage;
